import React from 'react';

/**
 * InfoBar — contextual callout
 *
 * Props:
 *  - type: 'info' | 'warning' | 'success' | 'danger' (default: 'info')
 *  - title?: string (optional heading shown above the content)
 *  - children?: React.ReactNode (preferred: pass JSX with links)
 *  - html?: string (optional raw HTML string; if provided, it will be injected)
 *  - style?: React.CSSProperties (optional style overrides)
 */
const PALETTES = {
  info:    { bg: '#e3f0ff', border: '#7cb9ff', text: '#052b47', accent: '#1c6ed2' },
  warning: { bg: '#fff7e6', border: '#ffd27a', text: '#583b00', accent: '#ff9f1a' },
  success: { bg: '#eaf8f0', border: '#a6e3c5', text: '#0d3d2a', accent: '#23a26d' },
  danger:  { bg: '#fdeaea', border: '#ffb1b1', text: '#5f0a0a', accent: '#ff4747' },
};

const ICONS = {
  info: (
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  ),
  warning: (
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
  ),
  success: (
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1 15l-5-5 1.41-1.41L11 13.17l6.59-6.59L19 8l-8 9z" />
  ),
  danger: (
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 13h-2V7h2v8zm0 4h-2v-2h2v2z" />
  ),
};

const InfoBarStyles = () => (
  <style>{`
    .vm-infobar-wrap{ position:relative; overflow:visible; }
    .vm-infobar{ position:relative; overflow:hidden; }

    /* Stronger link contrast inside the infobar */
    .vm-infobar a{ color: var(--accent); font-weight: 700; text-decoration: underline; text-underline-offset: 2px; }
    .vm-infobar a:hover, .vm-infobar a:focus{ text-decoration-thickness: 2px; filter: saturate(1.1) contrast(1.05); }
  `}</style>
);

const InfoBar = ({ type = 'info', title, children, html, style, animate = true }) => {
  const palette = PALETTES[type] || PALETTES.info;

  const container = {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    backgroundColor: palette.bg,
    color: palette.text,
    borderRadius: 10,
    padding: '12px 14px',
    margin: '16px auto',
    maxWidth: 1200,
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
    '--accent': palette.accent,
    '--text': palette.text,
    ...style,
  };

  const iconWrap = {
    flex: '0 0 auto',
    width: 28,
    height: 28,
    borderRadius: 6,
    // backgroundColor: palette.accent,  <-- removed this line
    display: 'grid',
    placeItems: 'center',
    marginTop: 2,
    boxShadow: 'none',
  };

  const heading = {
    margin: '0 0 6px 0',
    fontSize: 18,
    lineHeight: 1.2,
    fontWeight: 700,
  };

  const body = {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.5,
  };

  return (
    <>
      <InfoBarStyles />
      <div className={`vm-infobar-wrap${animate ? ' anim-reveal' : ''}`}>
        <div role="note" aria-live="polite" style={container} className="vm-infobar">
          <div style={iconWrap} aria-hidden>
            <svg viewBox="0 0 24 24" width="18" height="18" fill={palette.accent}>{ICONS[type] || ICONS.info}</svg>
          </div>
          <div className="info-text" style={{ flex: 1 }}>
            {title && <h3 style={heading}>{title}</h3>}
            {html ? (
              <div style={body} dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <div style={body}>{children}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InfoBar;
